// filepath: apps/web/src/admin/locations/detail.tsx
//
// ISS-515 — LocationDetailScreen.
//
// Read-only detail view. Renders the Go-aligned fields (parent
// short-id + name, code, status with Badge, closed_at, created_at,
// row_version, created_by_subject) + an Archive button that POSTs
// /api/directory/locations/{id}/close (the SPA-facing verb is
// "Archive"; the Go verb is "close" per BA spec).
//
// INV-7: the Archive button carries row_version; the server
// returns 409 row_version_mismatch on stale submission and the
// SPA surfaces a StaleVersionError (reload to fetch fresh
// row_version).
//
// INV-9: no dangerouslySetInnerHTML; every value flows through
// JSX text / design-system primitives.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDatePattern } from "@/i18n/format";
import { Card } from "@/design-system/Card";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  archiveLocation,
  getLocation,
  newIdempotencyKey,
  type Location,
} from "./api";

function handleSpaNavigate(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
): void {
  if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return;
  }
  e.preventDefault();
  navigate(href);
}

function shortId(uuid: string | null): string {
  if (!uuid) return "—";
  return uuid.slice(0, 8);
}

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

function isRowVersionConflict(e: unknown): boolean {
  if (e instanceof ApiError) {
    return e.status === 409 && e.code === "row_version_mismatch";
  }
  return false;
}

export interface LocationDetailScreenProps {
  id?: string;
  /** Test override for the loader. */
  loader?: (id: string) => Promise<Location>;
  /** Test override for the archive submit pipeline. */
  archiver?: (
    id: string,
    expectedRowVersion: number,
    key: string,
  ) => Promise<void>;
}

export function LocationDetailScreen({
  id = "",
  loader,
  archiver,
}: LocationDetailScreenProps = {}): React.ReactElement {
  const { t, locale } = useTranslation();
  const [row, setRow] = useState<Location | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [archiving, setArchiving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [stale, setStale] = useState<boolean>(false);
  const [archived, setArchived] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    setStale(false);
    setArchived(false);
    try {
      const f = loader ?? ((lid: string) => getLocation(lid));
      const r = await f(id);
      setRow(r);
    } catch (e) {
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  const onArchive = useCallback(async () => {
    if (!row) return;
    setArchiving(true);
    setError(null);
    setStale(false);
    try {
      const key = newIdempotencyKey();
      const a = archiver ?? archiveLocation;
      await a(row.id, row.row_version, key);
      setArchived(true);
      // Re-fetch to surface the new status='closed' and
      // closed_at timestamp the server just wrote.
      await load();
    } catch (e) {
      if (isRowVersionConflict(e)) {
        setStale(true);
      } else if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setArchiving(false);
    }
  }, [row, archiver, load]);

  if (loading) {
    return (
      <p data-testid="location-detail-loading">
        {t("directory.locations.detail.loading", "Loading location…")}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="location-detail-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="location-detail-error">
        {t("directory.locations.detail.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="location-detail-not-found">
        {t("directory.locations.detail.not_found", "Location not found")}
      </p>
    );
  }

  return (
    <section data-testid="location-detail-screen">
      <header>
        <h1 id="location-detail-title">{row.name}</h1>
        <div className="location-detail-actions">
          <a
            href={`/admin/locations/${encodeURIComponent(row.id)}/edit`}
            data-testid="location-detail-edit-link"
            onClick={(e) =>
              handleSpaNavigate(
                e,
                `/admin/locations/${encodeURIComponent(row.id)}/edit`,
              )
            }
            style={{ textDecoration: "none" }} // design-adherence-ignore: link reset (button-styled anchor; no underline)
          >
            <Button variant="primary">
              {t("directory.locations.detail.edit", "Edit")}
            </Button>
          </a>
          <a
            href="/admin/locations"
            data-testid="location-detail-back-link"
            onClick={(e) => handleSpaNavigate(e, "/admin/locations")}
            style={{ textDecoration: "none" }} // design-adherence-ignore: link reset
          >
            <Button variant="secondary">
              {t("directory.locations.detail.back", "Back")}
            </Button>
          </a>
        </div>
      </header>

      {archived ? (
        // design-adherence-ignore: <Banner> doesn't accept
        // data-testid; hook it on a wrapping span.
        <span data-testid="location-detail-archived">
          <Banner tone="info">
            {t("directory.locations.detail.archived", "Location archived.")}
          </Banner>
        </span>
      ) : null}
      {stale ? (
        <StaleVersionError
          entityName={row.name}
          onReload={() => void load()}
          data-testid="location-detail-stale"
        />
      ) : null}

      <Card>
        <dl>
          <dt>{t("directory.locations.detail.name", "Name")}</dt>
          <dd>{row.name}</dd>
          <dt>{t("directory.locations.detail.code", "Code")}</dt>
          <dd>{row.code ?? "—"}</dd>
          <dt>{t("directory.locations.detail.parent", "Parent")}</dt>
          <dd>{shortId(row.parent_location_id)}</dd>
          <dt>{t("directory.locations.detail.status", "Status")}</dt>
          <dd>
            <Badge tone={row.status === "active" ? "success" : "neutral"}>
              {t(`directory.locations.status.${row.status}`, row.status)}
            </Badge>
          </dd>
          <dt>{t("directory.locations.detail.closed_at", "Closed at")}</dt>
          <dd>
            {row.closed_at
              ? formatDatePattern(new Date(row.closed_at), locale)
              : "—"}
          </dd>
          <dt>{t("directory.locations.detail.row_version", "Row version")}</dt>
          <dd>{row.row_version}</dd>
          <dt>{t("directory.locations.detail.created_at", "Created at")}</dt>
          <dd>{formatDatePattern(new Date(row.created_at), locale)}</dd>
          <dt>
            {t(
              "directory.locations.detail.created_by_subject",
              "Created by subject",
            )}
          </dt>
          <dd>{row.created_by_subject}</dd>
        </dl>
        {row.status === "active" ? (
          <div className="location-detail-archive-section">
            {/* design-adherence-ignore: <Button> doesn't accept
                data-testid; wrap in a span to keep the test hook. */}
            <span data-testid="location-detail-archive-button">
              <Button
                type="button"
                variant="danger"
                onClick={() => void onArchive()}
                disabled={archiving}
              >
                {archiving
                  ? t("directory.locations.detail.archiving", "Archiving…")
                  : t("directory.locations.detail.archive", "Archive")}
              </Button>
            </span>
          </div>
        ) : null}
      </Card>
    </section>
  );
}

export default LocationDetailScreen;
